
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';

export const extractColumnLetterSet = (cellKey: string): string | null => {
  const onlyAlphaCharRegex = /[a-zA-Z]+/g;
  const matches = cellKey.match(onlyAlphaCharRegex);
  return matches ? matches[0] : null;
};

export const VARIABLE_VALUE = 'Variable';
/**
 * This function determines the new value for a cell based on the ledger values for a given alias.
 * It handles boolean values by checking if any of the values are truthy.
 * It handles non-boolean values by checking if all values are the same.
 * If the values are not the same, it returns 'Variable'.
 * @param isBooleanType - A boolean indicating if the cell is a boolean.
 * @param uniqueLedgerValuesForAlias - A set of unique values for a given alias.
 * @param allLedgerValuesAreTheSame - A boolean indicating if all values are the same.
 * @returns The new value for the cell.
 */
export const determineNewCellValue = (
  isBooleanType: boolean,
  uniqueLedgerValuesForAlias: Set<any>,
  allLedgerValuesAreTheSame: boolean
): string => {
  const TRUTHY_VALUES = [true, '1', 1];
  if (isBooleanType) {
    return TRUTHY_VALUES.some(value => uniqueLedgerValuesForAlias.has(value)) ? '1' : '0';
  }

  if (allLedgerValuesAreTheSame) {
    return uniqueLedgerValuesForAlias.values().next().value;
  }

  return VARIABLE_VALUE;
};

/**
 * This function determines the new value for a cell based on the ledger values for a given alias.
 * It handles boolean values by checking if any of the values are truthy.
 * It handles non-boolean values by checking if all values are the same.
 * If the values are not the same, it returns 'Variable'.
 * It also parses the value to the correct type.
 * @param ledgerValuesForAlias - An array of values for a given alias.
 * @param cellType - The type of the cell.
 * @returns The new value for the cell.
 */
export const handleLedgerValue = (ledgerValuesForAlias: any[], cellType: CellValue) => {
  let newValueForCell: number | string | null = '';
  const uniqueLedgerValuesForAlias = new Set(ledgerValuesForAlias);
  const allLedgerValuesAreTheSame = new Set(uniqueLedgerValuesForAlias).size === 1;

  // for booleans if any one is true then the value is true else false
  newValueForCell = determineNewCellValue(
    cellType === 'boolean',
    uniqueLedgerValuesForAlias,
    allLedgerValuesAreTheSame
  );
  if (cellType === 'number' && typeof newValueForCell === 'string' && newValueForCell !== '') {
    newValueForCell = Number(newValueForCell);
  }
  return newValueForCell === '' ? null : newValueForCell;
};

export const LIQ_PREF_LEDGER_ITEM_NAME_ALIAS = 'name';
export const LIQ_PREF_LEDGER_ITEM_NAME_TITLE = 'Name';

export const calculateCellChanges = <T>(ledgerInfluencedCells: ICell[], ledgerItems: T[]) =>
  ledgerInfluencedCells.map(ledgerInfluencedCell => {
    const ledgerValuesForAlias = ledgerItems.map((ledgerItem: T) => ledgerItem[ledgerInfluencedCell.alias as keyof T]);
    const newValueForCell = handleLedgerValue(ledgerValuesForAlias, ledgerInfluencedCell.dbType);

    return {
      cell: ledgerInfluencedCell,
      value: newValueForCell,
    };
  });
